import { useTranslation } from 'react-i18next';

import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';
import Text from '@/components/ui/custom/dataDisplay/Text';
import InfinityList from '@/components/ui/custom/InfinityList';
import Klass from '@/models/Klass';
import ScheduledLesson from '@/models/ScheduledLesson';
import { StudentReportRow } from './StudentReportRow';
import { useActiveStudents } from './StudentReports.hook';
import { Loading, Table } from '../ui/daisy';
import { getErrorMessage } from '@/utils/getErrorMessage';
import joinReportAndEnrollment from '@/utils/scheduled-lesson-report/joinReportAndEnrollment';
interface StudentReportsProps {
  klass: Klass;
  homeworkId?: number;
  scheduledLesson: ScheduledLesson;
  editingAllowed?: boolean;
}

const COLS_SPAN = 6;

export function StudentsReportsTable({
  klass,
  scheduledLesson,
}: StudentReportsProps) {
  const { t } = useTranslation('translation', {
    keyPrefix: 'scheduledLessonReportModal',
  });

  const { t: tReport } = useTranslation('translation', {
    keyPrefix: 'manageScheduledLessonReport',
  });

  const {
    enrollments,
    reports,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    isInitialLoading: loadingEnrollments,
    error: enrollmentsError,
  } = useActiveStudents({
    active: true,
    klassId: klass.id,
    scheduledLesson,
  });

  const enrollmentReports = joinReportAndEnrollment({ enrollments, reports });

  const isEmpty =
    !loadingEnrollments && !enrollmentsError && !enrollments.length;

  return (
    <div className="flex flex-col gap-4">
      <h2 className="font-semibold leading-7 text-base">
        {t('attendanceList')}
      </h2>
      <InfinityList
        hasNextPage={hasNextPage}
        isFetchingNextPage={isFetchingNextPage}
        onReachEnd={fetchNextPage}
        className="max-h-96 rounded-2xl"
        scroll
      >
        <Table.Container className="overflow-visible rounded-2xl">
          <Table pinRows className="bg-base-200 rounded-2xl">
            <Table.Head>
              <Table.Row className="text-xs">
                <Table.HeaderCell className="w-[90%] rounded-tl-2xl bg-base-200 text-start">
                  {t('columns.student')}
                </Table.HeaderCell>
                <Table.HeaderCell className="w-[5%] text-start bg-base-200">
                  {t('columns.ocurrence')}
                </Table.HeaderCell>
                <Table.HeaderCell className="w-[5%] text-start rounded-tr-2xl bg-base-200">
                  {t('columns.presence')}
                </Table.HeaderCell>
              </Table.Row>
            </Table.Head>
            <Table.Body>
              <ConditionalRenderer condition={loadingEnrollments}>
                <Table.Row>
                  <Table.Cell colSpan={COLS_SPAN}>
                    <div className="flex w-full justify-center py-6">
                      <Loading color="primary" size="sm" />
                    </div>
                  </Table.Cell>
                </Table.Row>
              </ConditionalRenderer>

              <ConditionalRenderer condition={!!enrollmentsError}>
                <Table.Row>
                  <Table.Cell colSpan={COLS_SPAN}>
                    <Text
                      format="rubik-400"
                      className="flex w-full justify-center py-6 text-neutral"
                      text={getErrorMessage(enrollmentsError)}
                    />
                  </Table.Cell>
                </Table.Row>
              </ConditionalRenderer>

              <ConditionalRenderer condition={isEmpty}>
                <Table.Row>
                  <Table.Cell colSpan={COLS_SPAN}>
                    <Text
                      format="rubik-400"
                      className="flex w-full justify-center py-6 text-neutral"
                      text={tReport('noStudents')}
                    />
                  </Table.Cell>
                </Table.Row>
              </ConditionalRenderer>

              <ConditionalRenderer condition={!loadingEnrollments}>
                {enrollmentReports.map(({ enrollment, report }) => {
                  return (
                    <StudentReportRow
                      key={enrollment.id}
                      enrollment={enrollment}
                      report={report}
                      scheduledLesson={scheduledLesson}
                      klass={klass}
                    />
                  );
                })}
              </ConditionalRenderer>
            </Table.Body>
          </Table>
        </Table.Container>
      </InfinityList>
    </div>
  );
}
